import Vue from 'vue';

/**
 * Vue directive to be used in schedule to create vertical marquee effect based
 * on a condition This directive wraps or unwraps child element in marquee tag.
 * @see
 * {@link https://vuejs.org/v2/guide/custom-directive.html|Custom Directive}
 * @module marqueeWrapper
 * @example
 * <caption>html</caption>
 * <marquee height="100%" width="100%"
 *          direction="up" scrolldelay='5'
 *          behavior='scroll'
 *          :scrollamount="metadata.marquee.speed"
 *          v-marquee-wrapper="metadata.marquee.active">
 *    Content with marquee effect
 * </marquee>
 */
Vue.directive('marqueeWrapper', {
    /**
     * Vue directive bindings
     * @typedef {object} DirectiveBindings
     * @property {string} name
     * @property {string} rawName
     * @property {boolean} value
     * @property {string} expression
     * @property {Object.<string, (string|number|boolean)>} modifiers
     * @property {Object.<string, function>} def
     */

    /**
     * called only once, when the directive is first bound to the element.
     * This is where you can do one-time setup work.
     * @param {HTMLElement} element
     * @param {DirectiveBindings} bindings
     */
    bind: function bind(element, bindings) {
        /**
         * The Marquee element wraps the sibling element and starts marquee
         * @param {HTMLElement} el
         */
        bindings.def.wrap = function (el) {
            // Find all next siblings with data-moved and move back into el
            while (el.nextElementSibling && el.nextElementSibling.dataset.moved) {
                el.appendChild(el.nextElementSibling).removeAttribute('data-moved');
            }
            el.hidden = false;
            el.height = '100%'; //Make element take space

            if (!el.marqueeing) {
                el.start(); //start marquee
                el.marqueeing = true;
            }
        };

        /**
         * The Marquee element unwraps the child element and stops marquee
         * @param {HTMLElement} el
         */
        bindings.def.unwrap = function (el) {
            // Move all children of el outside and mark them with data-moved
            // attr
            Array.from(el.children).forEach(function (child) {
                el.insertAdjacentElement('afterend', child).setAttribute('data-moved', true);
            });

            if (el.marqueeing) {
                el.stop(); //stop marquee
                el.marqueeing = false;
            }
            el.hidden = true;
            el.height = 0; //Make element take no space
        };
    },

    /**
     * called when the bound element has been inserted into its parent node
     * (this only guarantees parent node presence, not necessarily in-document).
     * @param {HTMLElement} el
     * @param {DirectiveBindings} bindings
     */
    inserted: function inserted(el, bindings) {
        bindings.def[bindings.value ? 'wrap' : 'unwrap'](el);
    },

    /**
     * called after the containing component’s VNode has updated,
     * but possibly before its children have updated.
     * @param {HTMLElement} el
     * @param {DirectiveBindings} bindings
     */
    update: function update(el, bindings) {
        bindings.def[bindings.value ? 'wrap' : 'unwrap'](el);
    },
});
